/*
 * Tenue — global.js
 * Shared helpers and the custom elements every page needs. Components that only some
 * sections use live in their own files and are loaded by those sections.
 */
(function () {
  'use strict';

  const Tenue = (window.Tenue = window.Tenue || {});

  Tenue.debounce = function (fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait || 300);
    };
  };

  Tenue.fetchConfig = function (accept) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/${accept || 'json'}`,
      },
    };
  };

  Tenue.emit = function (name, detail) {
    document.dispatchEvent(new CustomEvent(`tenue:${name}`, { detail }));
  };

  Tenue.on = function (name, handler) {
    const listener = (event) => handler(event.detail, event);
    document.addEventListener(`tenue:${name}`, listener);
    return () => document.removeEventListener(`tenue:${name}`, listener);
  };

  Tenue.parseHTML = function (text) {
    return new DOMParser().parseFromString(text, 'text/html');
  };

  /* Returns the inner markup of a section returned by the Section Rendering API. */
  Tenue.sectionInnerHTML = function (html, selector) {
    const doc = Tenue.parseHTML(html);
    const element = selector ? doc.querySelector(selector) : doc.querySelector('.shopify-section');
    return element ? element.innerHTML : '';
  };

  Tenue.getFocusable = function (container) {
    const selector =
      'summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^="-"]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe';
    return Array.from(container.querySelectorAll(selector)).filter(
      (element) => element.getClientRects().length > 0
    );
  };

  let trapCleanup = null;

  Tenue.trapFocus = function (container, elementToFocus) {
    Tenue.removeTrapFocus();
    const onKeydown = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = Tenue.getFocusable(container);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeydown);
    trapCleanup = () => document.removeEventListener('keydown', onKeydown);
    (elementToFocus || Tenue.getFocusable(container)[0] || container).focus();
  };

  Tenue.removeTrapFocus = function (elementToFocus) {
    if (trapCleanup) trapCleanup();
    trapCleanup = null;
    if (elementToFocus) elementToFocus.focus();
  };

  Tenue.lockScroll = function (lock) {
    document.documentElement.style.overflow = lock ? 'hidden' : '';
  };

  Tenue.updateCartBubble = function (sections) {
    if (!sections || !sections['cart-icon-bubble']) return;
    const markup = Tenue.sectionInnerHTML(sections['cart-icon-bubble']);
    document.querySelectorAll('[data-cart-bubble]').forEach((bubble) => {
      bubble.innerHTML = markup;
    });
  };

  /* ------------------------------------------------------------------------
     <details-dropdown>: closes on outside click, Escape and focus loss.
     ------------------------------------------------------------------------ */
  if (!customElements.get('details-dropdown')) {
    class DetailsDropdown extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector(':scope > details');
        if (!this.details) return;
        this.summary = this.details.querySelector(':scope > summary');
        this.onOutsideClick = (event) => {
          if (!this.contains(event.target)) this.close();
        };
        this.onToggle = () => {
          const open = this.details.open;
          if (this.summary) this.summary.setAttribute('aria-expanded', String(open));
          if (open) {
            document.addEventListener('click', this.onOutsideClick);
            document.querySelectorAll('details-dropdown').forEach((other) => {
              if (other !== this && !other.contains(this) && typeof other.close === 'function') other.close();
            });
          } else {
            document.removeEventListener('click', this.onOutsideClick);
          }
        };
        this.onKeyup = (event) => {
          if (event.key !== 'Escape' || !this.details.open) return;
          event.stopPropagation();
          this.close();
          if (this.summary) this.summary.focus();
        };
        this.onFocusOut = (event) => {
          if (this.details.open && event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
        };
        this.details.addEventListener('toggle', this.onToggle);
        this.addEventListener('keyup', this.onKeyup);
        this.addEventListener('focusout', this.onFocusOut);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onOutsideClick);
      }

      close() {
        if (this.details && this.details.open) this.details.open = false;
      }
    }
    customElements.define('details-dropdown', DetailsDropdown);
  }

  /* ------------------------------------------------------------------------
     <menu-drawer>: mobile navigation built on <details>.
     ------------------------------------------------------------------------ */
  if (!customElements.get('menu-drawer')) {
    class MenuDrawer extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector(':scope > details');
        if (!this.details) return;
        this.summary = this.details.querySelector(':scope > summary');
        this.panel = this.details.querySelector('[data-drawer-panel]');
        this.details.addEventListener('toggle', () => this.onToggle());
        this.addEventListener('keyup', (event) => {
          if (event.key === 'Escape' && this.details.open) this.close();
        });
        this.querySelectorAll('[data-drawer-close]').forEach((element) =>
          element.addEventListener('click', () => this.close())
        );
      }

      onToggle() {
        const open = this.details.open;
        if (this.summary) this.summary.setAttribute('aria-expanded', String(open));
        Tenue.lockScroll(open);
        if (open && this.panel) {
          Tenue.trapFocus(this.panel);
        } else {
          Tenue.removeTrapFocus();
        }
      }

      close() {
        if (!this.details.open) return;
        this.details.open = false;
        if (this.summary) this.summary.focus();
      }
    }
    customElements.define('menu-drawer', MenuDrawer);
  }

  /* ------------------------------------------------------------------------
     <localization-form>: submits when a selector changes.
     ------------------------------------------------------------------------ */
  if (!customElements.get('localization-form')) {
    class LocalizationForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.querySelectorAll('select').forEach((select) =>
          select.addEventListener('change', () => this.form.submit())
        );
      }
    }
    customElements.define('localization-form', LocalizationForm);
  }

  /* ------------------------------------------------------------------------
     Dialog helpers: [data-dialog-open="id"] and [data-dialog-close].
     ------------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        dialog.showModal();
      }
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) dialog.close();
      return;
    }
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) event.target.close();
    }
  });
})();
